import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2, Loader2, X, Save, ArrowLeft, TrendingUp } from 'lucide-react';
import { interDepotService } from '../../../services/interDepotService';
import { isAdmin } from '../../../utils/permissions';
import { useAppContext } from '../../../context/AppContext';
import { PERIODS, periodLabel } from '../../../utils/financialPeriods';

// =============================================================================
// Budget Setup > Contractors  (Distribution)
//
// The transport contractor master. A master rather than free text on the rate
// line, because free text gives you "Fast Freight", "Fast Freight " and "Fast
// Frieght" as three carriers, and then nobody can answer what the business
// spends with any one of them.
//
// CROSS-SITE AND NOT VERSION-SCOPED. A carrier is a fact about the world, not
// about a budget scenario — the same company hauls for every site and every
// version. Only the RATES are version-scoped, and those live on the lane.
//
// Own fleet is deliberately NOT a row here. A rate line is either OWNED (no
// contractor) or CONTRACTED (names one), so "Own Fleet" as a pseudo-contractor
// would make the distinction ambiguous exactly where reporting needs it sharp.
//
// ONE EXCEPTION TO "NOT VERSION-SCOPED": the EXPECTED ANNUAL INCREASE. Every
// transporter comes back once a year wanting CPI plus something, and it lands
// on a date — "6% from July". Two columns here replace flipping every rate line
// that carrier touches to MONTHLY and retyping the back half of the year.
//
// It is stored PER BUDGET VERSION (bud_contractor_increases) even though it is
// edited on this page, because "we expect 6%" is an ASSUMPTION, not a fact
// about the carrier. Budget and LE have to be able to disagree about it, which
// they could not if it sat on md_contractors with the phone number.
//
// It is a STEP applied to the WHOLE rate, which is what makes it different from
// the diesel index: when a carrier puts a rate up 6%, all of it goes up 6%,
// whereas only part of a rate is fuel. The two multiply — and if a carrier's
// annual increase is how they recover fuel, that line should NOT also carry a
// fuel share, or diesel is counted twice.
// =============================================================================

const BLANK = { contractor_code: '', contractor_name: '', contact_name: '', contact_number: '', is_active: true, sort_order: 0, notes: '' };

const btn = 'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors';
const btnPrimary = 'inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 disabled:cursor-not-allowed shadow-sm transition-colors';
const field = 'w-full text-xs rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 text-slate-800 dark:text-slate-100 px-2 py-1.5 outline-none focus:ring-2 focus:ring-blue-500/40 disabled:opacity-40';

export default function Contractors() {
  const navigate = useNavigate();
  const { selectedRole, selectedVersion, fyStartMonth } = useAppContext();
  const canEdit = isAdmin(selectedRole);
  // No version, no assumption: the increase columns are greyed rather than
  // accepting input the save would silently drop.
  const [versionScoped, setVersionScoped] = useState(false);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [dirty, setDirty] = useState({});
  const [entry, setEntry] = useState(BLANK);
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await interDepotService.getContractors();
      setRows(res.data?.contractors || []);
      setVersionScoped(!!res.data?.version_id);
      setDirty({});
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Could not load contractors.');
    } finally {
      setLoading(false);
    }
  }, []);

  // Reloads on version change: the increase is version data, so the same page
  // shows different numbers under Budget and under an LE.
  useEffect(() => { load(); }, [load, selectedVersion]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(''), 4000);
    return () => clearTimeout(t);
  }, [toast]);

  const patch = (code, p) => {
    setRows((prev) => prev.map((r) => (r.contractor_code === code ? { ...r, ...p } : r)));
    setDirty((d) => ({ ...d, [code]: true }));
  };

  const save = async () => {
    const changed = rows.filter((r) => dirty[r.contractor_code]);
    if (!changed.length) return;
    setBusy(true);
    setError('');
    try {
      const res = await interDepotService.saveContractors(changed);
      setToast(res.message || 'Saved.');
      await load();
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setBusy(false);
    }
  };

  const add = async () => {
    if (!entry.contractor_code.trim() || !entry.contractor_name.trim()) {
      setError('A code and a name are both required.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await interDepotService.saveContractors([{ ...entry, contractor_code: entry.contractor_code.trim().toUpperCase() }]);
      setToast('Contractor added.');
      setEntry(BLANK);
      setAdding(false);
      await load();
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (code) => {
    setBusy(true);
    setError('');
    try {
      const res = await interDepotService.deleteContractor(code);
      setToast(res.message || 'Removed.');
      await load();
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="h-full flex flex-col font-sans bg-slate-100 dark:bg-slate-900">
      <div className="shrink-0 px-6 pt-6 pb-4">
        <button className="mb-3 inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200"
                onClick={() => navigate('/distribution')}>
          <ArrowLeft className="w-3.5 h-3.5" /> Distribution
        </button>

        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="m-0 text-2xl font-bold tracking-tight text-slate-900 dark:text-slate-100">Contractors</h1>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              The transport contractors a lane can be outsourced to, and what each is expected to put its rates up by this year.
              The contractor itself is cross-site and shared by every version; the <strong>expected increase is an assumption</strong>, so it belongs to
              the version you are in — Budget and an LE can hold different views of it.
            </p>
          </div>
          {canEdit && (
            <div className="flex items-center gap-2">
              <button className={btn} onClick={() => setAdding((a) => !a)} disabled={busy}>
                <Plus className="w-3.5 h-3.5" /> Add contractor
              </button>
              <button className={btnPrimary} onClick={save} disabled={busy || !Object.keys(dirty).length}>
                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save
              </button>
            </div>
          )}
        </div>

        {!canEdit && (
          <div className="mt-3 px-3 py-2 rounded-lg border border-amber-300 bg-amber-50 dark:border-amber-500/40 dark:bg-amber-500/10">
            <p className="m-0 text-xs text-amber-800 dark:text-amber-300">
              Read-only — maintaining the contractor list needs an administrative role.
            </p>
          </div>
        )}
        {!versionScoped && !loading && (
          <div className="mt-3 px-3 py-2 rounded-lg border border-slate-300 bg-slate-50 dark:border-slate-600 dark:bg-slate-800">
            <p className="m-0 text-xs text-slate-600 dark:text-slate-300">
              Pick a budget version in the top bar to set expected increases — an assumption has to belong to a scenario.
            </p>
          </div>
        )}
        {error && (
          <div className="mt-3 flex items-start justify-between gap-2 px-3 py-2 rounded-lg border border-red-300 bg-red-50 dark:border-red-500/40 dark:bg-red-500/10">
            <p className="m-0 text-xs text-red-700 dark:text-red-300">{error}</p>
            <button onClick={() => setError('')} className="text-red-400 hover:text-red-600"><X className="w-3.5 h-3.5" /></button>
          </div>
        )}
        {toast && (
          <div className="mt-3 px-3 py-2 rounded-lg border border-emerald-300 bg-emerald-50 dark:border-emerald-500/40 dark:bg-emerald-500/10">
            <p className="m-0 text-xs text-emerald-700 dark:text-emerald-300">{toast}</p>
          </div>
        )}

        {adding && canEdit && (
          <div className="mt-3 grid grid-cols-2 md:grid-cols-5 gap-2 items-end px-3 py-3 rounded-lg border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800">
            {[
              ['contractor_code', 'Code', 'FASTFRT'],
              ['contractor_name', 'Name', 'Fast Freight (Pty) Ltd'],
              ['contact_name', 'Contact', 'optional'],
              ['contact_number', 'Phone', 'optional'],
            ].map(([k, label, ph]) => (
              <div key={k}>
                <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">{label}</label>
                <input className={field} placeholder={ph} value={entry[k]}
                       onChange={(e) => setEntry((s) => ({ ...s, [k]: e.target.value }))} />
              </div>
            ))}
            <div className="flex items-center gap-2">
              <button className={btnPrimary} onClick={add} disabled={busy}>Add</button>
              <button className={btn} onClick={() => { setAdding(false); setEntry(BLANK); }}>Cancel</button>
            </div>
          </div>
        )}
      </div>

      <div className="flex-1 min-h-0 px-6 pb-6 overflow-y-auto">
        <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-x-auto">
          {loading ? (
            <div className="h-40 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
          ) : rows.length === 0 ? (
            <p className="m-0 py-10 text-sm text-slate-400 text-center">
              No contractors yet. Add the transporters you buy line-haul from.
            </p>
          ) : (
            <table className="w-full border-collapse text-xs">
              <thead>
                <tr className="bg-slate-100 dark:bg-slate-700/60 text-left">
                  {['Code', 'Name', 'Contact', 'Phone', 'Increase %', 'From', 'Applies to', 'Notes', 'Active', ''].map((h) => (
                    <th key={h} className="px-2 py-2 font-bold uppercase text-[10px] tracking-wider text-slate-500 dark:text-slate-400 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, idx) => (
                  <tr key={r.contractor_code} className={`border-t border-slate-100 dark:border-slate-700/60 ${idx % 2 ? 'bg-slate-50/50 dark:bg-slate-800/40' : ''} ${r.is_active ? '' : 'opacity-60'}`}>
                    <td className="px-2 py-1.5 font-mono font-semibold text-slate-700 dark:text-slate-200 whitespace-nowrap">{r.contractor_code}</td>
                    <td className="px-2 py-1.5 min-w-[200px]">
                      <input className={field} value={r.contractor_name || ''} disabled={!canEdit}
                             onChange={(e) => patch(r.contractor_code, { contractor_name: e.target.value })} />
                    </td>
                    <td className="px-2 py-1.5 min-w-[150px]">
                      <input className={field} value={r.contact_name || ''} disabled={!canEdit}
                             onChange={(e) => patch(r.contractor_code, { contact_name: e.target.value })} />
                    </td>
                    <td className="px-2 py-1.5 min-w-[130px]">
                      <input className={field} value={r.contact_number || ''} disabled={!canEdit}
                             onChange={(e) => patch(r.contractor_code, { contact_number: e.target.value })} />
                    </td>

                    {/* The expected annual increase. Editable by anyone who can
                        reach the page and has a version — it is a planning
                        assumption, not master data, and gating it behind an
                        admin role would put the LE in the wrong hands. */}
                    <td className="px-2 py-1.5">
                      <input className={`${field} text-right font-mono`} style={{ width: 84 }}
                             type="number" step="0.1" min="-99" max="100"
                             disabled={!versionScoped} placeholder="0.0"
                             title="Applied to the WHOLE rate from the month below. Leave at zero for a carrier holding its price."
                             value={r.increase_pct ?? 0}
                             onChange={(e) => patch(r.contractor_code, { increase_pct: e.target.value })} />
                    </td>
                    <td className="px-2 py-1.5">
                      <select className={field} style={{ width: 110 }} disabled={!versionScoped}
                              title="The month the increase lands in, and applies from. Period 1 escalates the whole year."
                              value={r.increase_period ?? ''}
                              onChange={(e) => patch(r.contractor_code, { increase_period: e.target.value === '' ? null : Number(e.target.value) })}>
                        <option value="">— never —</option>
                        {PERIODS.map((p) => (
                          <option key={p} value={p}>{periodLabel(p, fyStartMonth)}</option>
                        ))}
                      </select>
                    </td>
                    {/* How many rate lines this actually moves, both modules.
                        Zero is the answer to "I set 6% and nothing happened" —
                        either nothing is priced against them, or every line has
                        opted out. */}
                    <td className="px-2 py-1.5 whitespace-nowrap text-[11px]">
                      {(() => {
                        const n = (r.interdepot_lines || 0) + (r.secondary_lines || 0);
                        const live = Number(r.increase_pct) && r.increase_period;
                        if (!n) return <span className="text-slate-400">no rate lines</span>;
                        return (
                          <span className={live ? 'text-slate-600 dark:text-slate-300' : 'text-slate-400'}
                                title={`${r.interdepot_lines || 0} inter-depot, ${r.secondary_lines || 0} secondary`}>
                            {live && <TrendingUp className="inline w-3 h-3 mr-1 text-amber-600 dark:text-amber-400" />}
                            {n} line{n === 1 ? '' : 's'}
                          </span>
                        );
                      })()}
                    </td>

                    <td className="px-2 py-1.5 min-w-[180px]">
                      <input className={field} value={r.notes || ''} disabled={!canEdit}
                             onChange={(e) => patch(r.contractor_code, { notes: e.target.value })} />
                    </td>
                    <td className="px-2 py-1.5 text-center">
                      <input type="checkbox" checked={!!r.is_active} disabled={!canEdit}
                             onChange={(e) => patch(r.contractor_code, { is_active: e.target.checked })} />
                    </td>
                    <td className="px-2 py-1.5">
                      {/* A contractor already priced on a lane cannot be deleted
                          without orphaning that budget — deactivating keeps the
                          history and takes it out of the picker. */}
                      {canEdit && (
                        r.in_use ? (
                          <span className="text-[10px] text-slate-400" title="Priced on a lane — deactivate instead">in use</span>
                        ) : (
                          <button onClick={() => remove(r.contractor_code)} disabled={busy}
                                  className="p-1 rounded text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10" title="Remove">
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {!loading && rows.length > 0 && (
          <div className="mt-3 rounded-xl border border-sky-200 dark:border-sky-500/25 bg-sky-50/60 dark:bg-sky-500/5 p-3">
            <p className="m-0 text-[11px] text-slate-600 dark:text-slate-300">
              <strong>The increase is a step, applied to the whole rate.</strong> 6% from July means every rate line this carrier holds runs at its
              typed rate for periods 1&ndash;6 and at 6% more from period 7 on — across inter-depot and secondary at once, without flipping a single
              line to monthly rates. Set the month to <em>never</em> for a carrier holding its price, and turn it off on an individual line
              (<strong>Escl.</strong> on the rate tables) for a contract that is genuinely locked.
            </p>
            <p className="m-0 mt-1.5 text-[11px] text-slate-500 dark:text-slate-400">
              <strong>Do not double count fuel.</strong> This stacks on top of the diesel index: a rate ends up at
              <span className="mx-1 font-mono text-slate-700 dark:text-slate-200">base × diesel × increase</span>.
              If a carrier&rsquo;s annual increase is how they recover fuel — an all-in escalation rather than a separate fuel adjustment clause —
              give that line a fuel share of <strong>0</strong> on the rate table, or diesel is counted twice.
              A contract has one mechanism or the other, rarely both.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
